import moize from 'moize';
import { filter, values, chain, find, contains, pluck } from 'underscore';
import { Person } from '@material-ui/icons';
import { TYPE_EVENT } from 'enums/projectEnum';
import { TYPE_UNASSIGNED } from 'enums/resourceEnum';
import { UNASSIGNED as UNASSIGNED_EXTENSION } from 'enums/extensionShortIdEnum';
import { replaceKeywords } from 'utils/keywordsUtil';

export const PROJECT = {
    order: 1,
    display: 'Project',
    name: 'project',
    value: 'PROJECT',
    default: false,
    propItem: 'projects',
    propGroupItem: 'projectGroups',
    idProperty: '_id',
    filteredCustomFieldProp: 'filteredProjectCustomFields',
    icon: 'folder',
    supportingText: `Generate reports on project-specific metrics like time spent, budget tracking, spending, and profitability analysis.`,
    itemFilter: (projects, selectedItems) =>
        filter(
            projects,
            item => item.type !== TYPE_EVENT.value && selectedItems && !contains(pluck(selectedItems, '_id'), item._id)
        ),
    groupFilter: (projectGroups, replaceKeyWordsMap, selectedGroups) =>
        chain(projectGroups)
            .filter(
                group =>
                    group.criteria !== TYPE_EVENT.value &&
                    selectedGroups &&
                    !contains(pluck(selectedGroups, '_id'), group._id)
            )
            .map(group => ({
                ...group,
                name: replaceKeywords(replaceKeyWordsMap, group.name),
            }))
            .value(),
    additionalGroupProps: props => ({
        fields: { label: 'name', value: '_id' },
        title: `Groups`,
        subTitle: `Add ${props && props.projectKeyWord} Groups to Report`,
        groupSubItem: 'projects',
    }),
    additionalProps: props => ({
        fields: {
            label: 'name',
            value: '_id',
            additional: 'projectCode',
            wrapWithBrackets: true,
        },
        filterTitle: `Filter by ${props && props.projectKeyWord} or group name`,
        title: `${props && props.projectPluralKeyWord}`,
        icon: Person,
        subTitle: `Add ${props && props.projectPluralKeyWord} to Report`,
    }),
    extension: 'ALL',
    default2ndLevelGrouping: 'resource',
};

export const RESOURCE = {
    order: 2,
    display: 'Resource',
    name: 'resource',
    value: 'RESOURCE',
    propItem: 'resources',
    propGroupItem: 'resourceGroups',
    idProperty: '_id',
    filteredCustomFieldProp: 'filteredResourceCustomFields',
    icon: 'personOutline',
    supportingText: `Analyze team performance with insights on utilization, availability, hours worked, and related costs.`,
    itemFilter: (resources, selectedItems) =>
        filter(
            resources,
            item =>
                item.type !== TYPE_UNASSIGNED.value && selectedItems && !contains(pluck(selectedItems, '_id'), item._id)
        ),
    groupFilter: (resourceGroups, replaceKeyWordsMap, selectedGroups) =>
        chain(resourceGroups)
            .filter(
                group =>
                    group.criteria !== TYPE_UNASSIGNED.value &&
                    selectedGroups &&
                    !contains(pluck(selectedGroups, '_id'), group._id)
            )
            .map(group => ({
                ...group,
                name: replaceKeywords(replaceKeyWordsMap, group.name),
            }))
            .value(),
    additionalGroupProps: props => ({
        fields: { label: 'name', value: '_id' },
        title: `Groups`,
        subTitle: `Add ${props && props.resourceKeyWord} Groups to Report`,
        groupSubItem: 'resources',
    }),
    additionalProps: props => ({
        fields: {
            label: 'firstName',
            value: '_id',
            additional: 'lastName',
        },
        filterTitle: `Filter by ${props && props.resourceKeyWord} or group name`,
        title: `${props && props.resourcePluralKeyWord}`,
        subTitle: `Add ${props && props.resourcePluralKeyWord} to Report`,
    }),
    extension: 'ALL',
    default2ndLevelGrouping: 'project',
};

export const EVENT = {
    order: 4,
    display: 'Event',
    value: 'EVENT',
    name: 'event',
    default: false,
    propItem: 'projects',
    propGroupItem: 'projectGroups',
    idProperty: '_id',
    icon: 'event',
    supportingText: `Track non-working time such as sick leave, vacation, and other events impacting availability.`,
    itemFilter: (projects, selectedItems) =>
        filter(
            projects,
            item => item.type === TYPE_EVENT.value && selectedItems && !contains(pluck(selectedItems, '_id'), item._id)
        ),
    groupFilter: projectGroups => filter(projectGroups, group => group.criteria === TYPE_EVENT.value),
    additionalProps: props => ({
        fields: {
            label: 'name',
            value: '_id',
            additional: 'projectCode',
            wrapWithBrackets: true,
        },
        filterTitle: `Filter by ${props && props.projectKeyWord} or group name`,
        title: 'Events',
        subTitle: 'Add Events to Report',
    }),
    extension: 'ALL',
    default2ndLevelGrouping: 'resource',
};

export const UNASSIGNED = {
    order: 3,
    display: 'Unassigned',
    value: 'UNASSIGNED',
    name: 'unassigned',
    default: false,
    propItem: 'resources',
    propGroupItem: 'resourceGroups',
    idProperty: '_id',
    filteredCustomFieldProp: 'filteredResourceCustomFields',
    icon: 'unassigned',
    supportingText: `Identify unassigned project time, helping allocate resources and track pending tasks.`,
    itemFilter: (resources, selectedItems) =>
        filter(
            resources,
            item =>
                item.type === TYPE_UNASSIGNED.value && selectedItems && !contains(pluck(selectedItems, '_id'), item._id)
        ),
    groupFilter: resourceGroups => filter(resourceGroups, group => group.criteria === TYPE_UNASSIGNED.value),
    additionalProps: props => ({
        fields: {
            label: 'firstName',
            value: '_id',
        },
        filterTitle: `Filter by ${props && props.resourceKeyWord} or group name`,
        title: 'Unassigned Work',
        subTitle: `Add Unassigned work to Report`,
    }),
    extension: UNASSIGNED_EXTENSION,
    default2ndLevelGrouping: 'project',
};

export const BOOKING = {
    order: 5,
    display: 'Booking',
    value: 'BOOKING',
    name: 'booking',
    default: false,
    propItem: 'projects',
    propGroupItem: 'projectGroups',
    idProperty: '_id',
    icon: 'listAltAdd',
    supportingText: `Create detailed lists of individual bookings for precise tracking and review.`,
    itemFilter: () => [],
    groupFilter: () => [],
    additionalProps: () => ({}),
    extension: 'ALL',
    default2ndLevelGrouping: 'resource',
};

export const itemTypes = {
    PROJECT,
    EVENT,
    RESOURCE,
    UNASSIGNED,
    BOOKING,
};

export const replaceMap = {
    [EVENT.name]: PROJECT.name,
    [UNASSIGNED.name]: RESOURCE.name,
};

export const replaceMapWithBooking = {
    [EVENT.name]: PROJECT.name,
    [UNASSIGNED.name]: RESOURCE.name,
    [BOOKING.name]: EVENT.name,
};

export const findItemTypeByName = moize(name => find(itemTypes, itemType => name === itemType.name) || {}, {
    maxSize: 2,
});

export const getItemTypesByExtensions = () => {
    const result = values(itemTypes);
    result.sort((a, b) => a.order - b.order);
    return result;
};
